import { ShippableContainerItemFragment, WasteClassification } from "generated-graphql/graphql";

import { daysUntil } from "./shipmentDisplay";

export type ShippableContainerStreamGroup = {
  /** Empty for uncharacterized containers, which have no stream to key on. */
  streamId: string;
  streamName: string | null;
  classification: WasteClassification | null;
  /**
   * The nearest deadline the group holds, which is what orders it. Null when no
   * container in it has one — the same "not dated" case the server sorts last,
   * not a deadline of zero.
   */
  soonestDeadline: string | null;
  containers: ShippableContainerItemFragment[];
};

/**
 * Earlier of two deadlines; null only when neither side has one.
 *
 * Compared through `daysUntil` rather than as strings because the field is a
 * DateTime scalar typed `any` — a raw `<` would be comparing whatever the
 * transport happened to hand back. It is also the parse the chip beside the
 * heading uses, so the order and the number in it cannot disagree.
 */
function earlier(a: string | null, b: string | null | undefined): string | null {
  const bDays = daysUntil(b);
  if (bDays === null) {
    return a;
  }
  const aDays = daysUntil(a);
  if (aDays === null) {
    return b ?? null;
  }
  return bDays < aDays ? (b ?? null) : a;
}

/**
 * Containers grouped by waste stream, most urgent stream first.
 *
 * The stream is what the vendor manifests and what the contents grid groups by,
 * so every picker orders itself the same way — a flat list makes the reader
 * rebuild the grouping themselves to answer "is this whole stream going out?".
 *
 * Ordering follows the server's, which sorts shippable containers
 * `deadlineDate asc, nulls last` and calls it urgency-first. Grouping used to
 * re-sort the streams alphabetically on top of that, which threw the ordering
 * away at the only level the reader scans: the page promised the waste nearest
 * its deadline and could put that stream last. Streams with nothing dated sort
 * after dated ones rather than masquerading as due-now, and the name breaks
 * ties so the order is still stable when no deadline separates two streams.
 *
 * Lives apart from the row components it feeds so the module holding them stays
 * components-only, which is what Fast Refresh needs to reload a picker without
 * losing the selection the user has built up in it.
 */
export function groupContainersByStream(
  containers: ShippableContainerItemFragment[]
): ShippableContainerStreamGroup[] {
  const groups = new Map<string, ShippableContainerStreamGroup>();
  for (const container of containers) {
    const streamId = container.wasteStream?.id ?? "";
    const group = groups.get(streamId) ?? {
      streamId,
      streamName: container.wasteStream?.name ?? null,
      classification: container.wasteStream?.classification ?? null,
      soonestDeadline: null,
      containers: [],
    };
    group.containers.push(container);
    group.soonestDeadline = earlier(group.soonestDeadline, container.clock.effectiveDeadlineDate);
    groups.set(streamId, group);
  }
  return [...groups.values()].sort((a, b) => {
    const aDays = daysUntil(a.soonestDeadline);
    const bDays = daysUntil(b.soonestDeadline);
    // Undated last, whichever side it falls on.
    if (aDays === null || bDays === null) {
      if (aDays !== bDays) {
        return aDays === null ? 1 : -1;
      }
    } else if (aDays !== bDays) {
      return aDays - bDays;
    }
    // U+FFFF sorts after any real name, which puts the unnamed group last.
    return (a.streamName ?? "￿").localeCompare(b.streamName ?? "￿");
  });
}
